import React from "react";
import { useParams, Navigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ArrowRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import PageHero from "@/components/common/PageHero";
import { Reveal, RevealGroup, RevealItem } from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { useQuote } from "@/context/QuoteContext";
import { SERVICES } from "@/data/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const service = SERVICES.find((s) => s.slug === slug);

  useSEO({
    title: service ? `${service.name} — ${service.tagline}` : "Servicio",
    description: service ? service.intro : "",
    image: service?.image,
    path: `/servicios/${slug}`,
    jsonLd: service
      ? {
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.name,
          description: service.intro,
          provider: { "@type": "Organization", name: "GEMA" },
          areaServed: "Colombia",
        }
      : undefined,
  });

  if (!service) return <Navigate to="/servicios" replace />;

  const Icon = Icons[service.icon] || Icons.Wrench;

  return (
    <>
      <PageHero
        overline={service.tagline}
        title={service.name}
        subtitle={service.short}
        image={service.image}
        crumbs={[{ label: "Servicios", to: "/servicios" }, { label: service.name }]}
      />

      {/* Intro + items */}
      <section className="bg-gema-black py-20 md:py-28">
        <div className="container-gema grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal><SectionLabel>Descripción</SectionLabel></Reveal>
            <Reveal delay={0.1}>
              <h2 className="heading-lg text-white mt-6">¿En qué consiste?</h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-white/60 mt-6 leading-relaxed text-lg">{service.intro}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <h3 className="font-heading font-bold uppercase text-white mt-10 mb-4">Incluye</h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {service.items.map((it) => (
                  <li key={it} className="flex items-start gap-3 text-white/70">
                    <Check className="h-5 w-5 text-gema-red mt-0.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="border border-white/10 bg-gema-ink p-8 sticky top-28">
                <Icon className="h-10 w-10 text-gema-red" strokeWidth={1.5} />
                <h3 className="font-heading font-black uppercase text-2xl text-white mt-6">¿Solicitar este servicio?</h3>
                <p className="text-white/50 text-sm mt-3 leading-relaxed">
                  Cuéntanos tu necesidad y recibe una cotización a la medida, sin compromiso.
                </p>
                <button onClick={() => openQuote({ service: service.name })} data-testid="service-quote-btn" className="btn-primary w-full mt-6 group">
                  Solicitar Cotización
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-white py-20 md:py-28">
        <div className="container-gema">
          <Reveal><SectionLabel dark={false}>Beneficios</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="heading-lg text-black mt-6">Por qué elegir este servicio</h2></Reveal>
          <RevealGroup className="grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10 mt-12">
            {service.benefits.map((b, i) => (
              <RevealItem key={i}>
                <div className="bg-white p-8 h-full flex items-start gap-4">
                  <span className="font-heading font-black text-2xl text-gema-red">0{i + 1}</span>
                  <p className="text-black/70 leading-relaxed">{b}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* Process */}
      <section className="bg-gema-ink py-20 md:py-28">
        <div className="container-gema">
          <Reveal><SectionLabel>Proceso</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="heading-lg text-white mt-6">Cómo trabajamos</h2></Reveal>
          <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {service.process.map((p, i) => (
              <RevealItem key={i}>
                <div className="border-t-2 border-gema-red pt-6 h-full">
                  <span className="font-mono text-xs text-white/40 tracking-widest">PASO 0{i + 1}</span>
                  <h3 className="font-heading font-bold uppercase text-white mt-3">{p.step}</h3>
                  <p className="text-white/50 text-sm mt-2 leading-relaxed">{p.desc}</p>
                </div>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-gema-black py-20 md:py-28">
        <div className="container-gema max-w-3xl">
          <Reveal><SectionLabel>Preguntas Frecuentes</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="heading-lg text-white mt-6 mb-10">Resolvemos tus dudas</h2></Reveal>
          <Reveal delay={0.2}>
            <Accordion type="single" collapsible className="w-full" data-testid="service-faq">
              {service.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                  <AccordionTrigger className="text-left font-heading font-bold uppercase text-white text-base hover:text-gema-red hover:no-underline py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/60 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
